import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProfileTab from "./components/ProfileTab";
import AppliedJobsTab from "./components/AppliedJobsTab";
import CVBuilderTab from "./components/CVBuilderTab";

type TabKey = "profile" | "applied" | "cv";

export default function AccountPage() {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TabKey) || "profile";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/dang-nhap");
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const tabParam = searchParams.get("tab") as TabKey;
    if (tabParam && ["profile", "applied", "cv"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Đang tải...</p>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: "profile", label: "Thông tin cá nhân", icon: "ri-user-settings-line" },
    { key: "applied", label: "Việc đã ứng tuyển", icon: "ri-file-list-3-line" },
    { key: "cv", label: "CV của tôi", icon: "ri-file-text-line" },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar forceSolid />

      <main className="pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-5 md:mb-8">
            <h1 className="font-heading font-bold text-xl md:text-3xl text-foreground-950">
              Tài khoản của tôi
            </h1>
            <p className="text-xs md:text-sm text-foreground-500 mt-1">
              Quản lý thông tin cá nhân, CV và việc làm đã ứng tuyển
            </p>
          </div>

          {/* Mobile: scrollable tab pills */}
          <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 mb-6 md:mb-8 border border-background-200/70 overflow-x-auto -mx-1 px-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.key
                    ? "bg-white text-foreground-950 shadow-sm"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm md:text-base`}></i>
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-4 md:p-8">
            {activeTab === "profile" && <ProfileTab user={user} />}
            {activeTab === "applied" && <AppliedJobsTab user={user} />}
            {activeTab === "cv" && <CVBuilderTab user={user} />}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}